import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";


const statusColors = {
    pending_payment: "bg-yellow-100 text-yellow-800",
    confirmed: "bg-blue-100 text-blue-800",
    accepted: "bg-blue-100 text-blue-800",
    preparing: "bg-purple-100 text-purple-800",
    shipped: "bg-indigo-100 text-indigo-800",
    delivered: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-700",
};


export default function Orders() {
    const { t, lang } = useLang();

    const [orders, setOrders] = useState([]);
    const [loadingOrderId, setLoadingOrderId] =
        useState(null);

    const loadOrders = async () => {
        try {
            const response = await api.get(
                "/orders/mine"
            );

            const rawOrders = (
                response.data
                || []
            );

            const ordersWithInvoices = await Promise.all(
                rawOrders.map(
                    async (order) => {
                        if (
                            order.payment_status
                            !== "paid"
                        ) {
                            return {
                                ...order,
                                invoice_summary:
                                    null,
                            };
                        }

                        try {
                            const invoiceResponse =
                                await api.get(
                                    `/orders/${order.order_id}/invoices`
                                );

                            return {
                                ...order,
                                invoice_summary:
                                    invoiceResponse.data,
                            };

                        } catch {
                            return {
                                ...order,
                                invoice_summary:
                                    null,
                            };
                        }
                    }
                )
            );

            setOrders(
                ordersWithInvoices
            );
        } catch (error) {
            toast.error(
                formatApiError(
                    error.response?.data?.detail
                ) || error.message
            );
        }
    };

    useEffect(() => {
        loadOrders();
    }, []);

    const statusLabel = (order) => {
        if (
            order.payment_status === "refunded"
            || order.refund_status === "refunded"
        ) {
            return lang === "ar"
                ? "مسترجع"
                : "Refunded";
        }

        const status = order.status;

        if (lang === "ar") {
            const labels = {
                pending_payment: "بانتظار الدفع",
                confirmed: "مؤكد",
                accepted: "تم قبول الطلب",
                preparing: "قيد التجهيز",
                shipped: "تم الشحن",
                delivered: "تم التوصيل",
                rejected: "مرفوض",
                cancelled: "ملغي",
            };

            return labels[status] || status;
        }

        const labels = {
            pending_payment: "Awaiting payment",
            confirmed: "Confirmed",
            accepted: "Accepted",
            preparing: "Preparing",
            shipped: "Shipped",
            delivered: "Delivered",
            rejected: "Rejected",
            cancelled: "Cancelled",
        };

        return labels[status] || status;
    };

    const paymentLabel = (order) => {
        if (order.payment_status === "paid") {
            return lang === "ar"
                ? "مدفوع"
                : "Paid";
        }

        if (order.payment_status === "failed") {
            return lang === "ar"
                ? "فشل الدفع"
                : "Payment failed";
        }

        if (
            order.payment_status === "refunded"
        ) {
            return lang === "ar"
                ? "\u062a\u0645 \u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0627\u0644\u0645\u0628\u0644\u063a"
                : "Refunded";
        }

        if (
            order.payment_status === "cancelled"
        ) {
            return lang === "ar"
                ? "ملغي"
                : "Cancelled";
        }

        return lang === "ar"
            ? "غير مدفوع"
            : "Unpaid";
    };

    const invoiceStatusLabel = (invoice) => {
        if (
            invoice?.available
        ) {
            return lang === "ar"
                ? "\u0639\u0631\u0636 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629"
                : "View invoice";
        }

        return lang === "ar"
            ? "\u0645\u0639\u0627\u064a\u0646\u0629 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629 - \u0642\u064a\u062f \u0627\u0644\u062a\u062c\u0647\u064a\u0632"
            : "Invoice preview - being prepared";
    };


    const canContinuePayment = (order) => {
        return (
            order.payment_method === "moyasar"
            && order.status === "pending_payment"
            && order.payment_status !== "paid"
            && order.payment_status !== "failed"
            && order.payment_status !== "cancelled"
            && order.payment_status !== "refunded"
        );
    };

    const canCancel = (order) => {
        return (
            order.payment_method === "moyasar"
            && order.status === "pending_payment"
            && order.payment_status !== "paid"
        );
    };

    const cancelOrder = async (orderId) => {
        const confirmed = window.confirm(
            lang === "ar"
                ? "هل أنت متأكد من إلغاء الطلب؟ سيتم إعادة الكمية المحجوزة إلى المخزون."
                : "Cancel this order? Reserved stock will be returned."
        );

        if (!confirmed) {
            return;
        }

        setLoadingOrderId(orderId);

        try {
            await api.post(
                `/orders/${orderId}/cancel`
            );

            toast.success(
                lang === "ar"
                    ? "تم إلغاء الطلب وإعادة المخزون"
                    : "Order cancelled and stock restored"
            );

            await loadOrders();

        } catch (error) {
            toast.error(
                formatApiError(
                    error.response?.data?.detail
                ) || error.message
            );
        } finally {
            setLoadingOrderId(null);
        }
    };

    return (
        <Layout>
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
                <h1 className="text-3xl md:text-4xl font-black text-espresso">
                    {t("myOrders")}
                </h1>

                <div
                    className="mt-8 space-y-4"
                    data-testid="orders-list"
                >
                    {orders.length === 0 ? (
                        <div className="text-center py-20 text-muted-foreground bg-surface rounded-2xl">
                            {t("noResults")}
                        </div>
                    ) : (
                        orders.map((order) => (
                            <div
                                key={order.order_id}
                                className="bg-white border border-border rounded-2xl p-5"
                                data-testid={`order-${order.order_id}`}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="text-xs text-muted-foreground">
                                            #{order.order_id}
                                        </div>

                                        <div className="font-bold text-espresso mt-1">
                                            {new Date(
                                                order.created_at
                                            ).toLocaleDateString()}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        <span
                                            className={`text-xs font-bold px-3 py-1 rounded-full ${
                                                statusColors[
                                                    order.status
                                                ] || "bg-surface"
                                            }`}
                                        >
                                            {statusLabel(order)}
                                        </span>

                                        {order.payment_method ===
                                            "moyasar" && (
                                            <span
                                                className={`text-xs font-bold px-3 py-1 rounded-full ${
                                                    order.payment_status ===
                                                    "paid"
                                                        ? "bg-green-100 text-green-800"
                                                        : order.payment_status ===
                                                          "failed"
                                                        ? "bg-red-100 text-red-800"
                                                        : "bg-yellow-100 text-yellow-800"
                                                }`}
                                            >
                                                {paymentLabel(
                                                    order
                                                )}
                                            </span>
                                        )}

                                        <div className="text-lg font-black text-terracotta">
                                            {Number(
                                                order.total
                                            ).toFixed(2)}{" "}
                                            {t("SAR")}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                                    {order.items.map((item) => (
                                        <Link
                                            key={
                                                item.product_id
                                            }
                                            to={`/products/${item.product_id}`}
                                            className="text-muted-foreground hover:text-terracotta"
                                        >
                                            {item.title} ×{" "}
                                            {item.quantity}
                                        </Link>
                                    ))}
                                </div>

                                {order.invoice_summary
                                    ?.invoices
                                    ?.length > 0 && (
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {order.invoice_summary.invoices.map(
                                            (
                                                invoice,
                                                index
                                            ) => (
                                                <Button
                                                    key={
                                                        invoice.invoice_key
                                                        || `${order.order_id}-${index}`
                                                    }
                                                    asChild
                                                    variant="outline"
                                                    size="sm"
                                                    className={
                                                        invoice.available
                                                            ? "rounded-full border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
                                                            : "rounded-full border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                                    }
                                                >
                                                    <Link
                                                        to={`/orders/${order.order_id}/invoices/${invoice.seller_id}`}
                                                    >
                                                        {invoiceStatusLabel(
                                                            invoice
                                                        )}
                                                    </Link>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                )}

                                {(canContinuePayment(order)
                                    || canCancel(order)) && (
                                    <div className="mt-5 pt-4 border-t border-border flex flex-wrap gap-3">
                                        {canContinuePayment(
                                            order
                                        ) && (
                                            <Button
                                                asChild
                                                className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white"
                                            >
                                                <Link
                                                    to={`/payment/${order.order_id}`}
                                                >
                                                    {lang ===
                                                    "ar"
                                                        ? "إكمال الدفع"
                                                        : "Continue payment"}
                                                </Link>
                                            </Button>
                                        )}

                                        {canCancel(order) && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                disabled={
                                                    loadingOrderId ===
                                                    order.order_id
                                                }
                                                onClick={() =>
                                                    cancelOrder(
                                                        order.order_id
                                                    )
                                                }
                                                className="rounded-full"
                                            >
                                                {loadingOrderId ===
                                                order.order_id
                                                    ? "..."
                                                    : lang ===
                                                      "ar"
                                                    ? "إلغاء الطلب"
                                                    : "Cancel order"}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </Layout>
    );
}